<template>

    <div class="app-auth-code">

        <div class="verification-header">
            <div class="icon" v-html="$icon('password')"></div>

            <h1 class="title">{{ 'auth.code.title' | __ }}</h1>
            <p class="subtitle">{{ 'auth.code.sent_to' | __ }} - {{ form.phoneNumber }}</p>
        </div>

        <form class="form" @submit.prevent="send">

            <div class="form-error-message" v-if="errors.get('form')">
                {{ errors.get('form') }}
            </div>

            <div class="form-row">

                <div class="form-row">
                    <input
                        v-model="form.code[3]"
                        ref="input3"
                        maxlength="1"
                        @input="enforceMaxLength($event, 3)"
                        @keyup="focusNext($event, 3)"
                        type="number"
                        class="digit-input overflow-hidden"
                        :disabled="!form.code[2]"
                    />
                    <input
                        v-model="form.code[2]"
                        ref="input2"
                        maxlength="1"
                        @input="enforceMaxLength($event, 2)"
                        @keyup="focusNext($event, 2)"
                        type="number"
                        class="digit-input overflow-hidden"
                        :disabled="!form.code[1]"
                    />
                    <input
                        v-model="form.code[1]"
                        ref="input1"
                        maxlength="1"
                        @input="enforceMaxLength($event, 1)"
                        @keyup="focusNext($event, 1)"
                        type="number"
                        class="digit-input overflow-hidden"
                        :disabled="!form.code[0]"
                    />
                    <input
                        v-model="form.code[0]"
                        ref="input0"
                        maxlength="1"
                        @input="enforceMaxLength($event, 0)"
                        @keyup="focusNext($event, 0)"
                        type="number"
                        class="digit-input overflow-hidden"
                    />
                </div>
                <p
                    v-if="errors.get('code')"
                    class="message"
                >
                    {{ errors.get('code') }}
                </p>
            </div>

            <div class="message">
                {{ 'auth.code.didnt_get' | __ }}

                <span
                    v-if="counter !== null && !showResendButton"
                >
                    {{ 'auth.code.cool_down' | __ }} {{ counter }}
                </span>

                <a
                    v-else
                    class="button"
                    @click.prevent="sendCode"
                >
                    {{ this.sending ? 'auth.code.sending' : 'auth.code.send_again' | __ }}
                </a>
            </div>

            <div class="submit-row w-full">
                <app-button
                    :disabled="isButtonDisabled"
                    :loading="loading"
                    ref="submitButton"
                >
                    {{ `auth.code.${buttonKey}` | __ }}
                </app-button>
            </div>
        </form>

    </div>

</template>

<script>

export default {
    name: 'app-auth-code',

    props: {
        value: Object,
        buttonKey: {
            type: String,
            default: 'to_account'
        }
    },

    data() {
        return {
            loading: false,
            sending: false,
            image: '',
            errors: this.$api.errors(),
            quoteRequestId: window.yna.quoteRequestId,
            counter: null,
            showResendButton: true,
            form: {
                ...this.value,
                code: ['', '', '', '']
            },
        }
    },

    watch: {
        form() {
            this.$emit('input', this.form);
        }
    },

    mounted() {
        this.$refs.input0.focus();
    },

    computed: {
        isButtonDisabled() {
            return this.form.code.some(digit => digit === "");
        }
    },

    methods: {
        enforceMaxLength(event, index) {
            const value = this.form.code[index];
            if (value && value.length > 1) {
                this.form.code[index] = value.slice(0, 1);
            }
        },
        async sendCode() {
            this.sending = true;

            const response = await this.$api.post('auth/send', this.form);
            this.errors = response.errors();

            if (response.hasErrors()) {
                this.$emit('unsent');
                this.form = {
                    code: ['', '', '', '']
                }
            }

            this.sending = false;
            this.startCountdown(); // Start countdown after sending code
        },

        async send() {
            this.loading = true;
            let codeString = this.form.code.map(String).join('');

            const response = await this.$api.post('auth/login', {...this.form, code: codeString});
            this.errors = response.errors();

            if (!response.hasErrors() && response.data().redirect) {
                window.location.href = response.data().redirect;
            }

            this.loading = false;
        },

        startCountdown() {
            this.counter = 50; // Start countdown from 50
            this.showResendButton = false; // Hide resend button

            const countdownInterval = setInterval(() => {
                this.counter--; // Decrement counter

                if (this.counter <= 0) {
                    clearInterval(countdownInterval); // Stop interval
                    this.showResendButton = true; // Show resend button
                }
            }, 1000); // 1000ms = 1s

            this.$once('hook:beforeDestroy', () => {
                clearInterval(countdownInterval); // Ensure cleanup
            });
        },

        focusNext(event, index) {
            if (event.target.value.length === 1 && index < 3) {

                this.$nextTick(() => {
                    this.$refs[`input${index + 1}`].focus();
                });

            }
        },

    }

}

</script>

<style lang="scss" scoped>

.app-auth-code {

    .digit-input {
        width: 50px;
        height: 50px;
        border: 1px solid #ccc;
        margin: 0 10px;
        text-align: center;
        font-size: 24px;

        &:disabled {
            background-color: #f0f0f0;
        }

        @media screen and (max-width: $mobile) {
            margin: 0;

        }
    }



    .verification-header {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        margin-bottom: 40px;

        .title {
            font-size: 25px;
            font-weight: bold;
            line-height: 1.3;
            color: $black;
            margin: 30px 0 15px;

            @media all and (max-width: $mobile) {
                margin: 25px 0 15px;
                font-size: 25px;
            }

        }

        .subtitle {
            font-size: 18px;
            font-weight: $font-light;
            line-height: 1.32;
            color: $black;

            @media all and (max-width: $mobile) {
                font-size: 18px;
            }
        }
    }

}

.form-row {
    max-width: 300px;

    ::v-deep {
        label {
            position: absolute;
            top: -25px;
            left: 95px
        }
    }
}

.message {
    margin-top: 30px;
    margin-bottom: 40px;
    font-size: 20px;
    font-weight: bold;
    line-height: 1.3;
    color: $black;

    a {
        color: $primary-blue;
        margin-right: 20px;
        cursor: pointer;
    }
}

.form {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    text-align: center;
}

/* For hiding increment/decrement arrows on Firefox */
@layer utilities {
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type="number"] {
        -moz-appearance: textfield;
    }
}

</style>
